import {
  createContext,
  useState,
  useEffect,
  useContext,
  useCallback,
} from "react";

import { useNavigate } from "react-router-dom";
import http from "../api/http";

export const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

/* =========================================================
   HOME PATH BY ROLE
   (قبلاً داخل login بود؛ حالا جدا شده تا LoginPage هم بتواند
   بعد از انیمیشن موفقیت از آن استفاده کند)
========================================================= */

export const getHomePath = (profile) => {
  const role = profile?.role;

  if (profile?.is_superuser || role === "admin") return "/panel/admin";
  if (role === "support") return "/panel/supporter";
  if (role === "marketer") return "/panel/marketer";

  return "/panel/user";
};

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const navigate = useNavigate();

  /* =========================================================
     CLEAR AUTH
  ========================================================= */

  const clearAuth = useCallback(() => {
    localStorage.removeItem("token");
    localStorage.removeItem("refresh");
    localStorage.removeItem("role");

    setUser(null);
  }, []);

  /* =========================================================
     GET PROFILE
  ========================================================= */

  const getProfile = useCallback(async () => {
    try {
      const res = await http.get("/auth/profile/");

      const userData = res.data;

      setUser(userData);

      /*
       * Role را برای استفاده در بخش‌های مختلف نگه می‌داریم.
       */
      localStorage.setItem("role", userData.role || "");

      return userData;
    } catch (err) {
      console.error("Get profile error:", err);

      throw err;
    }
  }, []);

  /* =========================================================
     CHECK AUTH WHEN APP LOADS
  ========================================================= */

  useEffect(() => {
    let mounted = true;

    const checkAuth = async () => {
      const token = localStorage.getItem("token");
      const refresh = localStorage.getItem("refresh");

      /*
       * هیچ Tokenای نداریم.
       */
      if (
        !token ||
        token === "null" ||
        token === "undefined" ||
        !refresh ||
        refresh === "null" ||
        refresh === "undefined"
      ) {
        if (mounted) {
          setLoading(false);
        }

        return;
      }

      try {
        /*
         * اگر Access Token منقضی شده باشد،
         * http.js به صورت خودکار Refresh می‌کند.
         */
        await getProfile();
      } catch (err) {
        /*
         * اینجا فقط زمانی وارد می‌شویم که:
         *
         * 1. Refresh Token وجود نداشته باشد
         * 2. Refresh Token منقضی شده باشد
         * 3. Refresh Token نامعتبر باشد
         */

        console.error("Authentication check failed:", err);

        if (mounted) {
          clearAuth();
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    checkAuth();

    return () => {
      mounted = false;
    };
  }, [getProfile, clearAuth]);

  /* =========================================================
     LOGIN

     login(data)                       → ذخیره توکن + پروفایل + هدایت
     login(data, { redirect: false })  → بدون هدایت (LoginPage خودش
                                         بعد از انیمیشن هدایت می‌کند)
  ========================================================= */

  const login = async (data, { redirect = true } = {}) => {
    try {
      /*
       * Access Token
       */
      if (data?.access) {
        localStorage.setItem("token", data.access);
      }

      /*
       * Refresh Token
       */
      if (data?.refresh) {
        localStorage.setItem("refresh", data.refresh);
      }

      /*
       * اگر Login موفق بود،
       * اطلاعات واقعی کاربر را دریافت کن.
       */
      const profile = await getProfile();

      /*
       * هدایت بر اساس Role
       */
      if (redirect) {
        navigate(getHomePath(profile));
      }

      return profile;
    } catch (err) {
      console.error("Login error:", err);

      clearAuth();

      throw err;
    }
  };

  /* =========================================================
     LOGOUT
  ========================================================= */

  const logout = () => {
    /*
     * حذف Access Token
     */
    localStorage.removeItem("token");

    /*
     * حذف Refresh Token
     */
    localStorage.removeItem("refresh");

    /*
     * حذف Role
     */
    localStorage.removeItem("role");

    /*
     * حذف User از State
     */
    setUser(null);

    /*
     * رفتن به Login
     */
    navigate("/panel/login", { replace: true });
  };

  /* =========================================================
     CONTEXT
  ========================================================= */

  return (
    <AuthContext.Provider
      value={{
        user,
        setUser,
        loading,
        login,
        logout,
        getProfile,
        getHomePath,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
